import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  SkeletonText,
  HStack,
  Text,
} from "@chakra-ui/react";
import colors from "resources/theme/colors";
import useListFinancialCategory, {
  CategoriaFinanceiraItem,
} from "features/condominio/hooks/useListFinancialCategory";
import { useState, useMemo, useEffect, useCallback } from "react";
import CardDefault from "components/Cards/CardDefault";
import type { TreeProps } from "antd/es/tree";
import { RenderTreeData } from "./components/RenderTreeData";
import { renameBreadCrumbsAndOrder } from "./util/helper";
import useChangeFinancialCategory from "core/features/condominio/hooks/useChangeFinancialCategory";
import { useLoading } from "components/GlobalLoading";
import NodeTree from "./components/NodeTree";
import { cloneDeep } from "lodash";
import { showError } from "infra/helpers/alerta";
import OutlineButton from "ui/components/Button/OutlineButton";
import { exportExcelPlanoContas } from "./util/helper";
import ExportMenu from "ui/components/ExportMenu";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFPlanoContas from "./ExportarPDF";
import { useTenantInfo } from "core/features/relatorios/hooks/useTenantInfo";
import { useCurrentTenant } from "core/features/auth/tenant.store";


export default function PlanoContas({ tenant }: { tenant?: string }) {
  const {
    data: categorys,
    isLoading,
    isError,
    refetch,
    dataUpdatedAt,
  } = useListFinancialCategory(tenant);
  const {tenant: currentTenant} = useCurrentTenant();
  const [items, setItems] = useState<CategoriaFinanceiraItem[]>([]);
  const { mutate, isLoading: isLoadingMutation } = useChangeFinancialCategory();

  const { refetch: refetchTenantInfo, isFetching: isFetchingTenantInfo } = useTenantInfo(false);

  const _onExportExcel = useCallback(async () => {
    exportExcelPlanoContas({
      data: items,
      currentTenant: currentTenant,
    });
  }, [items, currentTenant]);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: currentTenant ? `Plano de Contas - ${currentTenant.nome}.pdf` : `Plano de Contas.pdf`,
    Component: PDFPlanoContas,
    loading: isFetchingTenantInfo,
  });

  const _onExportPdf = useCallback(async () => {
    refetchTenantInfo().then((tenantInfo) => {
      onExportPdf({
        ...tenantInfo,
        data: items,
      });
    });
  }, [refetchTenantInfo, onExportPdf, items]);

  useEffect(() => {
    if (categorys) {
      setItems(cloneDeep(categorys));
    } else {
      setItems([]);
    }
  }, [categorys, dataUpdatedAt]);

  const [draggable, setDraggable] = useState(false);

  const nodeList = useMemo(
    () => items?.map((v) => RenderTreeData(v, undefined, draggable)),
    [items, draggable]
  );

  useLoading(isLoadingMutation);
  const onDrop: TreeProps["onDrop"] = (info) => {
    const dropNode = info.node as typeof info.node & {
      data: CategoriaFinanceiraItem;
    };
    const dragNode = info.dragNode as typeof info.node & {
      data: CategoriaFinanceiraItem;
    };

    const dropPos = info.node.pos.split("-");

    if (dropPos.length === 2) {
      return;
    }

    const dropPosition =
      info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const dropKey = info.node.key as string;
    const dragKey = info.dragNode.key as string;

    if (dropNode.data.operacao !== dragNode.data.operacao) {
      return showError(
        "Não é possível mudar a operação da categoria financeira"
      );
    }

    const loop = (
      data: CategoriaFinanceiraItem[],
      id: string,
      callback: (
        node: CategoriaFinanceiraItem,
        i: number,
        data: CategoriaFinanceiraItem[]
      ) => void
    ) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].id === id) {
          return callback(data[i], i, data);
        }
        if (data[i].items) {
          loop(data[i].items!, id, callback);
        }
      }
    };
    const data = [...items];

    // Find dragObject
    let dragObj: CategoriaFinanceiraItem;
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    if (!info.dropToGap) {
      // Drop on the content
      loop(data, dropKey, (item) => {
        item.items = item.items || [];
        // where to insert
        item.items.unshift({
          ...dragObj,
          categoriaFinanceiraPaiId: item.id,
        });
      });
    } else {
      let ar: CategoriaFinanceiraItem[] = [];
      let i: number;
      loop(data, dropKey, (_item, index, arr) => {
        ar = arr;
        i = index;
      });
      if (dropPosition === -1) {
        ar.splice(i!, 0, {
          ...dragObj!,
          categoriaFinanceiraPaiId: ar[0]?.categoriaFinanceiraPaiId,
        });
      } else {
        ar.splice(i! + 1, 0, {
          ...dragObj!,
          categoriaFinanceiraPaiId: ar[0]?.categoriaFinanceiraPaiId,
        });
      }
    }
    renameBreadCrumbsAndOrder(data);
    setItems(data);
  };

  return (
    <CardDefault>
      <Box mb={2}>
        <HStack spacing=".3rem">
          <Text fontSize="18px" fontWeight="bold" as="span" mr={4}>
            Plano de contas
          </Text>
          {!draggable ? (
            <OutlineButton size="md" onClick={() => setDraggable(true)}>
              Reordenar categorias
            </OutlineButton>
          ) : (
            <>
              <Button
                variant="outline"
                color={colors.primary}
                borderColor={colors.primary}
                size="md"
                onClick={() => {
                  setItems(cloneDeep(categorys || []));
                  setDraggable(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                size="md"
                minW={"113px"}
                onClick={() => {
                  mutate(items, { onSuccess: () => setDraggable(false) });
                }}
                variant="solid"
                bgColor={colors.primary}
                color="white"
                _hover={{}}
              >
                Salvar
              </Button>
            </>
          )}

          {items.length > 0 ? (
            <ExportMenu
              onExportPdf={_onExportPdf}
              onExportarXlsx={_onExportExcel}
            />
          ) : null}
        </HStack>
      </Box>

      {isError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle mr={2}>Houve um erro!</AlertTitle>
          <AlertDescription>
            <Text as="span" mr={2}>
              Houve um problema ao realizar esta operação.
            </Text>
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      {draggable ? (
        <Box rounded={6} px={"16px"} py={"8px"} bg={colors.blue50} mb={4}>
          <Text color={colors.blue700}>
            Para reordenar clique no ícone da esquerda e mova a categoria até
            onde desejar.
          </Text>
          <Text color={colors.blue700}>
            Quando terminar todas as alterações, clique em Salvar.
          </Text>
        </Box>
      ) : null}
      <Box maxW={["100%", "100%", "95%", "85%"]}>
        {isLoading ? (
          <SkeletonText noOfLines={4} spacing={4} />
        ) : (
          <NodeTree draggable={draggable} onDrop={onDrop} children={nodeList} />
        )}
      </Box>
    </CardDefault>
  );
}

export interface FormFields {
  nome: string;
}
